import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePatient } from "@/context/PatientContext";
import {
  House, UsersThree, Stethoscope, CalendarBlank, Pill, Storefront,
  MapPin, IdentificationCard, Bell, CheckSquare, Camera, SignOut, UserCircle, Plus,
  ClockCounterClockwise, List as ListIcon
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose
} from "@/components/ui/sheet";

const NAV = [
  { to: "/app", label: "Dashboard", icon: House, end: true, testid: "nav-dashboard" },
  { to: "/app/patients", label: "Patients", icon: UsersThree, testid: "nav-patients" },
  { to: "/app/doctors", label: "Doctors", icon: Stethoscope, testid: "nav-doctors" },
  { to: "/app/appointments", label: "Appointments", icon: CalendarBlank, testid: "nav-appointments" },
  { to: "/app/medications", label: "Medications", icon: Pill, testid: "nav-medications" },
  { to: "/app/pill-scanner", label: "Pill Scanner", icon: Camera, testid: "nav-pill-scanner" },
  { to: "/app/pharmacies", label: "Pharmacies", icon: Storefront, testid: "nav-pharmacies" },
  { to: "/app/addresses", label: "Addresses", icon: MapPin, testid: "nav-addresses" },
  { to: "/app/insurance", label: "Insurance", icon: IdentificationCard, testid: "nav-insurance" },
  { to: "/app/reminders", label: "Reminders", icon: Bell, testid: "nav-reminders" },
  { to: "/app/todos", label: "To-do", icon: CheckSquare, testid: "nav-todos" },
  { to: "/app/timeline", label: "Timeline", icon: ClockCounterClockwise, testid: "nav-timeline" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { patients, activeId, setActiveId } = usePatient();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A2520] flex">
      {/* Sidebar (desktop) */}
      <aside className="w-64 shrink-0 border-r border-[#EBE8E0] bg-white/60 backdrop-blur-sm hidden lg:flex flex-col">
        <div className="px-6 py-6">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#2C5545] flex items-center justify-center">
              <Pill weight="duotone" className="text-white" size={22} />
            </div>
            <div className="font-heading font-extrabold text-xl tracking-tight">EverCara</div>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-full text-[15px] transition-colors ${
                  isActive
                    ? "bg-[#2C5545] text-white"
                    : "text-[#576A62] hover:bg-[#EBE8E0]/60 hover:text-[#1A2520]"
                }`
              }
            >
              <item.icon weight="duotone" size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#EBE8E0]">
          <button
            onClick={() => navigate("/app/profile")}
            data-testid="nav-profile"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-full hover:bg-[#EBE8E0]/60 transition-colors text-left"
          >
            <UserCircle weight="duotone" size={28} className="text-[#2C5545]" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name || "Caregiver"}</div>
              <div className="text-xs text-[#576A62] truncate">{user?.email}</div>
            </div>
          </button>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm text-[#C86A51] hover:bg-[#C86A51]/10 transition-colors"
          >
            <SignOut weight="duotone" size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-[#FDFBF7] flex flex-col" data-testid="mobile-nav-sheet">
          <SheetHeader className="px-6 py-6 border-b border-[#EBE8E0] text-left">
            <SheetTitle className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#2C5545] flex items-center justify-center">
                <Pill weight="duotone" className="text-white" size={22} />
              </div>
              <span className="font-heading font-extrabold text-xl tracking-tight">EverCara</span>
            </SheetTitle>
          </SheetHeader>
          <nav className="px-3 py-3 space-y-1 flex-1 overflow-y-auto">
            {NAV.map((item) => (
              <SheetClose asChild key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  data-testid={`mobile-${item.testid}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-full text-[15px] transition-colors ${
                      isActive
                        ? "bg-[#2C5545] text-white"
                        : "text-[#576A62] hover:bg-[#EBE8E0]/60 hover:text-[#1A2520]"
                    }`
                  }
                >
                  <item.icon weight="duotone" size={20} />
                  <span>{item.label}</span>
                </NavLink>
              </SheetClose>
            ))}
          </nav>
          <div className="p-4 border-t border-[#EBE8E0] bg-[#FDFBF7]">
            <SheetClose asChild>
              <button
                onClick={() => navigate("/app/profile")}
                data-testid="mobile-nav-profile"
                className="w-full flex items-center gap-3 px-3 py-2 rounded-full hover:bg-[#EBE8E0]/60 transition-colors text-left"
              >
                <UserCircle weight="duotone" size={28} className="text-[#2C5545]" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{user?.name || "Caregiver"}</div>
                  <div className="text-xs text-[#576A62] truncate">{user?.email}</div>
                </div>
              </button>
            </SheetClose>
            <SheetClose asChild>
              <button
                data-testid="mobile-logout-btn"
                onClick={logout}
                className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm text-[#C86A51] hover:bg-[#C86A51]/10 transition-colors"
              >
                <SignOut weight="duotone" size={18} /> Sign out
              </button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#EBE8E0]">
          <div className="flex items-center justify-between px-4 md:px-6 py-4 gap-3 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-btn"
                aria-label="Open navigation"
                className="lg:hidden p-2 -ml-1 rounded-full hover:bg-[#EBE8E0]/60 transition-colors shrink-0"
              >
                <ListIcon size={22} weight="bold" className="text-[#1A2520]" />
              </button>
              <div className="lg:hidden font-heading font-extrabold text-lg truncate">EverCara</div>
            </div>
            <div className="flex-1 flex justify-end items-center gap-2 md:gap-3 min-w-0 overflow-hidden">
              {patients.length > 0 ? (
                <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                  <span className="hidden sm:inline text-xs uppercase tracking-widest text-[#576A62] shrink-0">Patient</span>
                  <Select value={activeId} onValueChange={setActiveId}>
                    <SelectTrigger
                      data-testid="patient-switcher"
                      className="min-w-0 max-w-[220px] rounded-full border-[#EBE8E0] bg-white h-11 [&>span]:truncate"
                    >
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`patient-option-${p.id}`}>
                          {p.name} <span className="text-[#576A62] text-xs">({p.relationship || p.role || "—"})</span>
                          {p.is_shared && <span className="text-[#3E7B5C] text-xs ml-1">· shared</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <button
                onClick={() => navigate("/app/patients?new=1")}
                data-testid="add-patient-header-btn"
                className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#2C5545] text-white px-4 py-2 text-sm font-semibold hover:bg-[#224235] transition-colors shrink-0"
              >
                <Plus size={16} weight="bold" /> Add patient
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
